import Signal from "badmfck-signal";
import { useEffect, useState } from "react";

export interface INav {
	path: string;
	id: string;
	label: string;
	page: JSX.Element;
	sub?: INav[];
	icon?: JSX.Element;
	roles?: string[];
}

export interface IBreadcrumbs {
	path: string;
	node: INav;
}

const S_NAV_CHANGED = new Signal<INav>();

export const Inner = () => {
	const nav = S_NAV_CHANGED.use([]);
	if (nav && nav.page) return nav.page;
	return <></>;
};

export class Router {
	currentPath: string;
	currentParams: Record<string, string> = {};
	currentNav: INav | null = null;
	breadcrumbs: IBreadcrumbs[] = [];
	#sitemap: INav[];
	#originalPushState: History["pushState"] | null = null;
	#originalReplaceState: History["replaceState"] | null = null;
	#patchedPushState: History["pushState"] | null = null;
	#patchedReplaceState: History["replaceState"] | null = null;
	static instance: Router | null = null;

	constructor(sitemap: INav[]) {
		this.#sitemap = sitemap;
		this.currentPath = window.location.pathname;
		if (Router.instance) return;
		Router.instance = this;
		this.#patchHistoryMethods();
		window.addEventListener("popstate", this.#onPopState);
		window.addEventListener("locationchange", this.#onLocationChange);
		S_NAV_CHANGED.onSubscribe = cb => this.currentNav && cb!(this.currentNav);
		this.#processPath();
	}

	navigate(id: string, params?: { [key: string]: string }, queryParams?: { [key: string]: string }) {
		let path = this.#getPath(id, params);

		if (!path.startsWith("/")) path = "/" + path;

		if (queryParams) {
			const query = new URLSearchParams(queryParams).toString();
			if (query) path += "?" + query;
		}

		if (typeof path === "string") {
			if (window.location.pathname + window.location.search !== path) {
				history.pushState({}, "", path);
			}
		}
	}

	#getPath(id: string, params?: { [key: string]: string }): string {
		if (!this.#sitemap) {
			console.warn("No sitemap");
			return "no-sitemap/" + id;
		}

		let node = this.#findNode(this.#sitemap, id);

		if (!node) {
			console.warn(id + " not found in sitemap");
			return "not-found/" + id;
		}

		const path: string[] = [];
		while (node) {
			// generate path
			path.push(node.path);
			node = (node as any).parent;
		}

		let addr = path.reverse().join("/");
		if (params) for (const i in params) addr = addr.replaceAll(":" + i, params[i]);

		return addr;
	}

	#findNode(nodes: INav[], id: string, parent?: INav): INav | null {
		let result = null;
		for (const i of nodes) {
			if (i.id === id) {
				result = i;
				break;
			}
			if (i.sub) {
				const r = this.#findNode(i.sub, id, i);
				if (r) {
					result = r;
					break;
				}
			}
		}
		if (parent && result && !(result as any).parent) {
			(result as any).parent = parent;
		}
		return result;
	}

	#patchHistoryMethods() {
		this.#originalPushState = history.pushState;
		this.#originalReplaceState = history.replaceState;
		const originalPushState = this.#originalPushState;
		const originalReplaceState = this.#originalReplaceState;
		this.#patchedPushState = function (...args) {
			const result = originalPushState.apply(history, args);
			window.dispatchEvent(new Event("locationchange"));
			return result;
		};
		this.#patchedReplaceState = function (...args) {
			const result = originalReplaceState.apply(history, args);
			window.dispatchEvent(new Event("locationchange"));
			return result;
		};
		history.pushState = this.#patchedPushState;
		history.replaceState = this.#patchedReplaceState;
	}

	#onPopState = (_: PopStateEvent) => {
		this.#onLocationChange();
	};

	#onLocationChange = () => {
		this.currentPath = window.location.pathname;
		this.#processPath();
	};

	#getLegacyRedirectPath() {
		const match = window.location.pathname.match(/^\/cards\/transactions\/([^/]+)$/);
		if (!match?.[1]) return null;

		return `/transactions?cardId=${encodeURIComponent(match[1])}`;
	}

	#processPath() {
		if (!this.currentPath || !this.currentPath.length) {
			return;
		}

		const legacyRedirectPath = this.#getLegacyRedirectPath();
		if (legacyRedirectPath) {
			history.replaceState({}, "", legacyRedirectPath);
			return;
		}

		const params: Record<string, string> = {};
		let path = this.currentPath.split("/").filter((val: string) => val !== "" && val !== null);
		let nodes = this.#sitemap;
		let found: INav | null = null;
		this.breadcrumbs = [];

		if (path.length === 0) {
			path = ["/"];
		}

		for (let i = 0; i < path.length; i++) {
			const p = path[i];

			// iterate over nodes
			for (const j of nodes) {
				// simple node
				if (j.path === p) {
					//console.log("found simple node ", p);
					this.breadcrumbs.push({
						path: p,
						node: j,
					});
					found = j;
					if (j.sub) nodes = j.sub;
					break;
				}

				if (j.path.startsWith(":")) {
					//console.log("Found param: ", j.path, p);
					this.breadcrumbs.push({
						path: p,
						node: j,
					});
					params[j.path.substring(1)] = p;
					found = j;
					if (j.sub) nodes = j.sub;
					break;
				}

				if (j.path.indexOf("/") !== -1) {
					// split complex node
					const subnodes = j.path.split("/");
					if (p === subnodes[0]) {
						//console.log("found complex node ", j.path, p);
						i++;
						params[subnodes[1].substring(1)] = path[i];
						this.breadcrumbs.push({
							path: p,
							node: j,
						});
						this.breadcrumbs.push({
							path: path[i],
							node: j,
						});
						found = j;
						if (j.sub) nodes = j.sub;
						break;
					}
				}
			}
		}

		if (found) {
			this.currentParams = params;
			this.currentNav = found;
			//console.log("123!", this.breadcrumbs);
			queueMicrotask(() => {
				//console.log("123!", this.breadcrumbs);
				S_NAV_CHANGED.invoke(found);
			});
		}
	}

	destroy() {
		this.#sitemap = [];
		window.removeEventListener("popstate", this.#onPopState);
		window.removeEventListener("locationchange", this.#onLocationChange);
		if (this.#originalPushState && history.pushState === this.#patchedPushState) {
			history.pushState = this.#originalPushState;
		}
		if (this.#originalReplaceState && history.replaceState === this.#patchedReplaceState) {
			history.replaceState = this.#originalReplaceState;
		}
		Router.instance = null;
	}
}

export const isRouteAllowed = (breadcrumbs: IBreadcrumbs[], role?: string | null) => {
	if (!role || breadcrumbs.length === 0) return false;
	return breadcrumbs.every(({ node }) => !node.roles || node.roles.includes(role));
};

export const isNavAllowed = (nav?: INav | null, role?: string | null) =>
	Boolean(nav && role && (!nav.roles || nav.roles.includes(role)));

export const useRouter = () => {
	const [router, setRouter] = useState<Router>(Router.instance!);
	useEffect(() => {
		const handler = S_NAV_CHANGED.subscribe((_: any) => {
			setRouter(Router.instance!);
		});
		return () => {
			S_NAV_CHANGED.unsubscribe(handler);
		};
	}, []);
	return router;
};

export const useBreadcrumbs = () => {
	const [breadcrumbs, setBreadcrumbs] = useState<IBreadcrumbs[]>(() => [...(Router.instance?.breadcrumbs ?? [])]);
	useEffect(() => {
		const handler = S_NAV_CHANGED.subscribe((_: any) => {
			setBreadcrumbs([...(Router.instance?.breadcrumbs ?? [])]);
		});
		return () => {
			S_NAV_CHANGED.unsubscribe(handler);
		};
	}, []);
	return breadcrumbs;
};

export const useCurrentPath = () => {
	const [currentPath, setCurrentPath] = useState<{
		currentPath: string;
		currentParams: Record<string, string>;
		currentNav: INav;
	} | null>(() => Router.instance?.currentNav
		? {
				currentNav: Router.instance.currentNav,
				currentPath: Router.instance.currentPath,
				currentParams: { ...Router.instance.currentParams },
			}
		: null);
	useEffect(() => {
		const handler = S_NAV_CHANGED.subscribe((_: any) => {
			setCurrentPath({
				currentNav: _,
				currentPath: Router.instance?.currentPath ?? "",
				currentParams: Router.instance?.currentParams ?? {},
			});
		});
		return () => {
			S_NAV_CHANGED.unsubscribe(handler);
		};
	}, []);
	return currentPath;
};

export const useCreateRouter = (sitemap: INav[], view: React.ReactElement) => {
	useEffect(() => {
		setTimeout(() => {}, 1000);
		new Router(sitemap);
		return () => Router.instance?.destroy();
	}, [sitemap]);
	return view;
};
